<template>
  <div  @click.capture="checkHideContextMenu">
    <div
      class="appview-container"
      :class="{
        'resizing': !!initialMousePositionOnResize,
      }"
      @mousemove="resizeDrag"
      @mouseup="resizeStop">
      <!-- RESIZE DRAG HELPER DIV -->
      <div
        class="resizer"
        :style="{
          'left': `max(calc(10vw - 0.25rem), min(calc(${sidebarWidth} - 0.25rem), calc(50vw - 0.25rem)))`,
        }"
        @mousedown="resizeStart" />

      <!-- SIDEBAR -->
      <div
        class="sidebar-container"
        :style="{
          'flex-basis': sidebarWidth,
        }">
        <Sidebar />
      </div>

      <!-- MAIN -->
      <div class="view-container">
        <router-view :key="$route.name" />
      </div>

      <!-- INTRO -->
      <b-modal
        v-model="showIntroModal"
        has-modal-card>
        <div class="modal-card">
          <div class="modal-card-head">
            <span class="modal-card-title">
              {{ introPage === 1 ? `Welcome to Byblio, ${welcomeName}!` : 'A Byblio explainer' }}
            </span>
          </div>
          <div class="modal-card-body">
            <div class="sub-header">
              about byblio
            </div>
            Byblio (from the Latin <em>bybliotheca</em>) serves as your online library where you can organize, store, and annotate your documents, store links, and create notes.<br><br>
            Below you can choose whether to start a short introduction to Byblio or to look around yourself. You can always start the introduction via
            <span class="welcome-sidebar-ref"><i class="mdi mdi-account-circle" /> {{ sidebarAccountText }}</span> in the sidebar.
          </div>
          <div class="modal-card-foot">
            <button class="button" @click="StopIntro">
              I'll look around myself
            </button>
            <button class="button" @click="NextIntroStep">
              Start the introduction!
            </button>
          </div>
        </div>
      </b-modal>
      <div :style="`position: absolute; bottom: 5rem; left: ${sidebarWidth}; height: 10px; width: 10px;`">
        <b-tooltip
          :active="introStep > 1"
          position="is-right"
          type="is-secondary-dark"
          multiline
          always>
          <template v-slot:content>
            <div class="intro-tooltip-text" v-show="introStep === 2">
              In the sidebar, you can navigate between collections. We already created a collection for you. You can create and manage collections via
               <span class="tooltip-sidebar-item"><i class="mdi  mdi-rhombus-split" /> My collections</span> in the sidebar. <!-- eslint-disable-line -->
            </div>
            <div class="intro-tooltip-text" v-show="introStep === 3">
              Documents or links are saved in cards. You can add a card by clicking on the button in the bottom-right corner. You can also add multiple files at once.
            </div>
            <div class="intro-tooltip-text" v-show="introStep === 4">
              On top of the page, you can navigate between cards and notes.
            </div>
            <div class="intro-tooltip-text" v-show="introStep === 5">
              If you navigated to Notes, clicking on the button in the bottom-right corner adds a new note.
            </div>
            <div class="intro-navigation-container">
              <span class="intro-navigation" @click.stop="StopIntro">stop tour</span>
              <span class="intro-navigation" v-show="introStep === 2" style="opacity: 0.6; cursor: default;">previous</span>
              <span class="intro-navigation" v-show="introStep > 2" @click.stop="PreviousIntroStep">previous</span>
              <span class="intro-navigation" @click.stop="NextIntroStep">next</span>
            </div>
          </template>
        </b-tooltip>
      </div>

    </div>

    <!-- ITEM VIEW -->
    <ItemView v-if="appIsReady" />

  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import ItemView from '@/components/AppView/ItemView/ItemView.vue';
import Sidebar from '@/components/AppView/Sidebar/Sidebar.vue';
// eslint-disable-next-line import/extensions
import { EventBus } from '@/EventBus.js';
const throttle = require('lodash/throttle');

export default {
  name: 'AppView',
  components: {
    ItemView,
    Sidebar,
  },
  data() {
    return {
      initialMousePositionOnResize: null,
      currentSidebarWidth: null,
      introPage: 0,
    };
  },
  computed: {
    ...mapState({
      notification: state => state.notifications.notification,
      storedSidebarWidth: state => state.appState.localSettings.sidebarWidth,
      introStep: state => state.appState.introStep,
      name: state => state.users.userInfo.name,
      firstName: state => state.users.userInfo.firstName,
      lastName: state => state.users.userInfo.lastName,
      appIsReady: state => state.appState.appIsReady,
      fileData: state => state.files.webViewerFileData,
      openFile: state => state.files.openFile,
    }),
    ...mapGetters({
      gotError: 'notifications/getError',
    }),
    welcomeName() {
      if (this.name) return this.name;
      if (this.firstName && this.lastName) return `${this.firstName} ${this.lastName}`;
      if (this.firstName) return this.firstName;
      if (this.lastName) return this.lastName;
      return this.email.substr(0, this.email.indexOf('@'));
    },
    sidebarAccountText() {
      if (this.name) return this.name;
      if (this.firstName && this.lastName) return `${this.firstName} ${this.lastName}`;
      if (this.firstName) return this.firstName;
      if (this.lastName) return this.lastName;
      return this.email;
    },
    showIntroModal() {
      return this.introStep === 1;
    },
    sidebarWidth() {
      return this.currentSidebarWidth || this.storedSidebarWidth;
    },
  },
  methods: {
    ...mapMutations({
      setSidebarWidth: 'appState/SetSidebarWidth',
      PreviousIntroStep: 'appState/PreviousIntroStep',
      NextIntroStep: 'appState/NextIntroStep',
      StopIntro: 'appState/StopIntro',
    }),
    checkHideContextMenu(e) {
      // If user clicks anywhere that is not a contextMenuClick, check if we need to hide it
      const { className } = e.target;
      const isContextMenuClick = className.includes('collection-list-item-menu-item') || className.includes('collection-list-item-menu-icon') || className.includes('new-section-input') || className.includes('collection-list-item-menu-sub');
      if (!isContextMenuClick) {
        EventBus.$emit('check-hide-context-menu', e);
      }
    },
    resizeStart($event) {
      this.initialMousePositionOnResize = $event.x;
    },
    resizeDrag: throttle(function ($event) { // eslint-disable-line
      if (!this.initialMousePositionOnResize) return;

      const { x } = $event;
      const newWidth = Math.min(x, document.body.clientWidth / 2);
      this.currentSidebarWidth = `${newWidth}px`;
    }, 10),
    resizeStop() {
      if (!this.initialMousePositionOnResize) return;

      this.initialMousePositionOnResize = null;
      this.setSidebarWidth(this.currentSidebarWidth);
    },
  },
  mounted() {
    // Use case: when upon starting the app an invalid request for a card/note/collection is made, the notification is set before the watcher (below) is active. Hence an
    // additional check on mounted.
    if (this.notification) {
      const msg = this.notification; // Copy before removeNotification mutation destroys stuff
      setTimeout(() => {
        this.$buefy.toast.open({
          message: msg,
          type: 'is-warning',
        });
      }, 400);
    }

    // to do: make this a named function, and destroy listener in beforeDestroy()
    document.addEventListener('keydown', (event) => {
      const { key } = event;

      if (key === 'Escape') {
        // Blur active input element, if it's there
        const activeEl = document.activeElement;
        if (activeEl.tagName !== 'BODY') activeEl.blur();
        // Hide context menu
        EventBus.$emit('check-hide-context-menu', event);
      }
    });
  },
  watch: {
    gotError: function() { // eslint-disable-line
      if (this.gotError.error) {
        let message = `An error occurred.<br><span style="font-size: 0.85rem;">You may want to reload Byblio (F5) if you encounter problems.</span><br><span style="font-size:0.7rem;">(${this.gotError.code})</span>`;
        if (this.gotError.customMessage) message = this.gotError.customMessage;

        this.$buefy.snackbar.open({
          indefinite: true,
          message,
          position: 'is-bottom',
          type: 'is-warning',
        });
      }
    },
    notification() {
      if (!this.notification) return;

      this.$buefy.toast.open({
        message: this.notification,
        type: 'is-warning',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/layouts';

.resizer {
  position: absolute;
  height: 100%;
  width: 0.75rem;
  z-index: 10;
  cursor: w-resize;
  // border: 1px solid red;
}
.resizing {
  user-select: none;
  cursor: w-resize;
}
.appview-container {
  /* most are default values, just here to be explicit */
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-around;
  align-items: stretch;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: $lightgrey;
}
.sidebar-container {
  flex-basis: 14vw;
  min-width: 10vw;
  max-width: 80vw;
  height: 100vh;
  overflow: hidden;
  flex-grow: 0;
  flex-shrink: 0;
  transition: width 0.3s;
}
.view-container {
  position: relative;
  background-color: $white;
  border-radius: 0.75rem 0 0 0;
  box-shadow: -1px 0 2.5px 2px $darkgrey;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100vh;
  max-height: 100vh;
  z-index: 1;
  min-width: 50vw;
  flex-grow: 1;
  flex-shrink: 1;
}

// Classes for introduction tutorial thing
.sub-header {
  font-size: 0.9rem;
  font-family: $quattrocentoSans;
  font-variant: small-caps;
  font-style: normal;
  color: $black;
  font-weight: bold;
  padding: 0.25rem 0;
}
.sub-header:not(:first-of-type) {
  padding: 0.75rem 0 0.25rem;
}
.modal-card {
  font-family: $headerFont;
  text-align: justify;
}
.welcome-sidebar-ref {
  border: 1px solid #dbdbdb;
  padding: 0.25rem;
  background: #efefef;
}
.modal-card-foot {
  display: flex;
  justify-content: center;
}
.intro-tooltip-text {
  font-family: $headerFont;
  font-size: 0.9rem;
  white-space: normal;
}
.intro-navigation-container {
  margin-top: 0.5rem;
  border-top: 1px solid #bbbbbb;
  text-align: right;
  width: 50vw;
}
.intro-navigation {
  cursor: pointer;
  color: $primary-light;
  font-size: 0.85rem;
  line-height: 0.85rem;
  font-variant: small-caps;
  padding: 0 0.25rem;
}
.intro-navigation:hover {
  background: lighten($secondary-dark, 5);
}
.tooltip-sidebar-item {
  border: 1px solid #dbdbdb;
  background: #efefef;
  padding: 0.25rem;
  color: $black;
}

// TRANSITIONS (not sure if necessary still, this css is also scoped...)
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.quick-fade-enter-active,
.quick-fade-leave-active {
  transition: opacity 0.1s;
}
.fade-enter,
.fade-leave-to,
.quick-fade-enter,
.quick-fade-leave-to {
  opacity: 0;
}
</style>
